import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { Trash2, Heart, ArrowUpDown, Filter, ChevronRight, MapPin } from "lucide-react";
import { translateText } from "../utils/categoryTranslations";
import { saveFavorites, getCachedFavorites } from '../utils/offlineDB';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const [refresh, setRefresh] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const { user, accessToken, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [infoPopup, setInfoPopup] = useState(null);
  const [isOffline, setIsOffline] = useState(false);
  //const language = 'en';

  const translations = {
    el: {
      title: "Τα αγαπημένα μου",
      filter: "Φίλτρα",
      sort: "Ταξινόμηση",
      category: "Κατηγορία",
      city: "Πόλη",
      area: "Περιοχή",
      selectCat: "Επιλογή κατηγορίας",
      selectCity: "Επιλογή πόλης",
      selectArea: "Επιλογή περιοχής",
      sortBy: "Ταξινόμηση κατά",
      datePub: "Ημερομηνία προσθήκης",
      price: "Τιμή",
      name: "Όνομα προσφοράς",
      rating: "Βαθμολογία",
      status: "Κατάσταση",
      store: "Όνομα καταστήματος",
      asc: "Αύξουσα",
      desc: "Φθίνουσα",
      az: "Α-Ω",
      za: "Ω-Α",
      noFavs: "Δεν υπάρχουν αγαπημένα ακόμα.",
      revFavs: "Αφαίρεση από τα αγαπημένα",
      actions: "Ενέργειες",
      discounts: "Προσφορές",
      location: "Όνομα καταστήματος",
      statusActive: "Ενεργή",
      statusExpired: "Ληγμένη",
      loadMore: "Φόρτωση περισσότερων...",
      nmDiscounts: "Δεν υπάρχουν άλλες προσφορές",
      statusFilter: "Κατάσταση προσφοράς",
      allStatuses: "Όλες",
      onlyActive: "Μόνο ενεργές",
      onlyExpired: "Μόνο ληγμένες",
      photoFilter: "Φωτογραφία",
      allPhotos: "Όλες",
      withPhoto: "Με φωτογραφία",
      withoutPhoto: "Χωρίς φωτογραφία",
      priceRange: "Εύρος τιμής",
      minPrice: "Από (€)",
      maxPrice: "Έως (€)",
      dateRange: "Εύρος ημερομηνιών",
      dateFrom: "Από",
      dateTo: "Έως",
      noStoreLocation: "Αυτή η προσφορά δεν διαθέτει τοποθεσία καταστήματος.",
      infoTitle: "Πληροφορία",
      ok: "Εντάξει"
    },
    en: {
      title: "My favourites list",
      filter: "Filter",
      sort: "Sort",
      category: "Category",
      city: "City",
      area: "Area",
      selectCat: "Select category",
      selectCity: "Select city",
      selectArea: "Select area",
      sortBy: "Sort by",
      datePub: "Date added",
      price: "Price",
      name: "Offer name",
      rating: "Rating",
      status: "Status",
      store: "Store name",
      asc: "Ascending",
      desc: "Descending",
      az: "A-Z",
      za: "Z-A",
      noFavs: "No favourites yet.",
      revFavs: "Remove from favourites",
      actions: "Actions",
      discounts: "Offers",
      location: "Store name",
      statusActive: "Active",
      statusExpired: "Expired",
      loadMore: "Loading more...",
      nmDiscounts: "No more offers",
      statusFilter: "Offer status",
      allStatuses: "All",
      onlyActive: "Active only",
      onlyExpired: "Expired only",
      photoFilter: "Photo",
      allPhotos: "All",
      withPhoto: "With photo",
      withoutPhoto: "Without photo",
      priceRange: "Price range",
      minPrice: "From (€)",
      maxPrice: "To (€)",
      dateRange: "Date range",
      dateFrom: "From",
      dateTo: "To",
      noStoreLocation: "This discount does not have a store location.",
      infoTitle: "Info",
      ok: "OK"
    }
  };

  const t = translations[language] || translations.el;

  const isAlphabeticalSortField = (field) => ["title", "status", "store_name"].includes(field);
  const getOrderLabel = (value) => {
    if (isAlphabeticalSortField(sortBy)) {
      return value === "asc" ? t.az : t.za;
    }
    return value === "asc" ? t.asc : t.desc;
  };

  const getStatusLabel = (status) => {
    if (status === "Active") return t.statusActive;
    if (status === "Expired") return t.statusExpired;
    return status;
  };

  // Sorting & Filtering States
  const [sortVisible, setSortVisible] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterArea, setFilterArea] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterHasPhoto, setFilterHasPhoto] = useState("all");
  const [filterPriceMin, setFilterPriceMin] = useState("");
  const [filterPriceMax, setFilterPriceMax] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterOptions, setFilterOptions] = useState({ categories: [], cities: [], areas: [] });

  //Fetch favorites with all applied filters and sorting
  const fetchFavorites = async (pageToLoad = 1) => {
    try {
      if (pageToLoad === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const offset = (pageToLoad - 1) * pageSize;
      const params = new URLSearchParams({ lang: language, sortBy, order, limit: pageSize, offset });
      if (filterCategory.length > 0) params.append("categories", filterCategory.join(","));
      if (filterCity) params.append("city", filterCity);
      if (filterArea.length > 0) params.append("areas", filterArea.join(","));
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterHasPhoto !== "all") params.append("hasPhoto", filterHasPhoto);
      if (filterPriceMin !== "") params.append("priceMin", filterPriceMin);
      if (filterPriceMax !== "") params.append("priceMax", filterPriceMax);
      if (filterDateFrom) params.append("dateFrom", filterDateFrom);
      if (filterDateTo) params.append("dateTo", filterDateTo);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/favorites/myfavorites?${params.toString()}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();

      const fetched = data.favorites || [];
      if (pageToLoad === 1) {
        setFavorites(fetched);
        setIsOffline(false);
        saveFavorites(fetched).catch(() => {});
      } else {
        setFavorites(prev => [...prev, ...fetched]);
      }

      setHasMore(fetched.length === pageSize);
      if (pageToLoad === 1) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    } catch (err) {
      console.error(err);
      if (pageToLoad === 1) {
        const cached = await getCachedFavorites();
        if (cached.length > 0) {
          setFavorites(cached);
          setIsOffline(true);
        }
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      setFavorites([]);
      setPage(1);
      setHasMore(true);
      fetchFavorites(1);
    }
  }, [filterCategory, filterCity, filterArea, filterStatus, filterHasPhoto, filterPriceMin, filterPriceMax, filterDateFrom, filterDateTo, sortBy, order, accessToken, language, refresh]);

  useEffect(() => {
    if (!accessToken) return;
    if (page === 1) return;
    fetchFavorites(page);
  }, [page, accessToken]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPage((prev) => prev + 1);
      }
    }, {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore]);

  //Fetch Initial Filter Options
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/favorites/filter-options?lang=${language}`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const json = await res.json();
        setFilterOptions({
          categories: json.categories || [],
          cities: json.cities || [],
          areas: []
        });
      } catch (err) { console.error(err); }
    };
    if (accessToken) fetchFilters();
  }, [accessToken, language]);

  //Fetch Areas when City changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (!filterCity) { setFilterOptions(prev => ({ ...prev, areas: [] })); return; }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/discounts/areasByCity?city=${filterCity}&lang=${language}`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const json = await res.json();
        setFilterOptions(prev => ({ ...prev, areas: json.areas || [] }));
      } catch (err) { console.error(err); }
    };
    fetchAreas();
  }, [filterCity, accessToken, language]);

  const toggleSelection = (value, state, setState) => {
    state.includes(value) ? setState(state.filter(v => v !== value)) : setState([...state, value]);
  };

  //when the refresh changes the function gets retriggered
  //function that reloads the list
  const refreshFavorites = () => setRefresh((prev) => !prev);

  //Remove favorite
  const handleRemoveFavorite = async (discountId) => {
    if (!user || !accessToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/favorites/${discountId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to remove favorite");

      //refresh after delete
      refreshFavorites();

    } catch (error) {
      console.error(`Error removing discount ${discountId}:`, error);
    }
  };

  //mobile expand
  const toggleExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const handleShowOnMap = (item) => {
    if (!item.full_store || !item.full_store.latitude || !item.full_store.longitude) {
      setInfoPopup({ title: t.infoTitle, msg: t.noStoreLocation });
      return;
    }

    const s = item.full_store;

    // Select and prepare data based on language preference for ShowOnMap component
    const mapData = {
      discountTitle: item.title,
      storeName: language === 'el' ? s.store_name_el : s.store_name_en,
      lat: s.latitude,
      lng: s.longitude,
      city: language === 'el' ? s.city_el : s.city_en,
      area: language === 'el' ? s.area_el : s.area_en,
      road: language === 'el' ? s.road_el : s.road_en,
    };

    navigate("/showonmap", { state: mapData });
  };

  const handleViewDiscount = (discountId) => {
    navigate("/viewdiscount", {
      state: { discountId }
    });
  };

  // useEffect(() => {
  //   const fetchFilterOptions = async () => {
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_BACKEND_URL}/favorites/filter-options?lang=${currentLang}`
  //       );

  //       if (!response.ok) throw new Error("Failed to fetch filter options");

  //       const data = await response.json();
  //       setFilterOptions(data);
  //     } catch (err) {
  //       console.error("Error fetching filter options:", err);
  //     }
  //   };

  //   fetchFilterOptions();
  // }, []);

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background ">
        <ProfileSidebar />
        <main id="favorites-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 xl:ml-20 text-center md:text-left">

          <div className="flex justify-between items-center mb-8 gap-6 w-full max-w-6xl mt-12 md:mt-0">
            <h2 className="text-2xl font-semibold text-custom-letters flex items-center gap-2">
              <Heart className="text-custom-grad1" /> {t.title}
            </h2>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setFiltersVisible(prev => !prev)}
                title={t.filter}
                className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full flex items-center justify-center"
              >
                <Filter size={18} />
              </button>


              <button
                onClick={() => setSortVisible(prev => !prev)}
                title={t.sort}
                className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full"
              >
                <ArrowUpDown size={18} />
              </button>
            </div>
          </div>
          {/* FILTER PANEL (Dropdowns) */}
          {filtersVisible && (
            <div className="bg-custom-header p-6 rounded-xl shadow mb-6 w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 animate-in fade-in duration-300 border border-gray-100">
              {/* Category */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.category}</p>
                <select
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && !filterCategory.includes(val)) setFilterCategory([...filterCategory, val]);
                    e.target.value = "";
                  }}
                  className="w-full p-2 rounded border text-sm"
                >
                  <option value="">{t.selectCat}</option>
                  {filterOptions.categories?.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {language === 'el' ? cat.category_name_el : cat.category_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.statusFilter}</p>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full p-2 rounded border text-sm"
                >
                  <option value="all">{t.allStatuses}</option>
                  <option value="Active">{t.onlyActive}</option>
                  <option value="Expired">{t.onlyExpired}</option>
                </select>
              </div>

              {/* City */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.city}</p>
                <select
                  value={filterCity}
                  onChange={(e) => {
                    setFilterCity(e.target.value);
                    setFilterArea([]);
                  }}
                  className="w-full p-2 rounded border text-sm"
                >
                  <option value="">{t.selectCity}</option>
                  {filterOptions.cities?.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Area */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.area}</p>
                <select
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && !filterArea.includes(val)) setFilterArea([...filterArea, val]);
                    e.target.value = "";
                  }}
                  disabled={!filterCity}
                  className={`w-full p-2 rounded border text-sm ${!filterCity ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <option value="">{t.selectArea}</option>
                  {filterOptions.areas?.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>

              {/* Photo Filter */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.photoFilter}</p>
                <select
                  value={filterHasPhoto}
                  onChange={(e) => setFilterHasPhoto(e.target.value)}
                  className="w-full p-2 rounded border text-sm"
                >
                  <option value="all">{t.allPhotos}</option>
                  <option value="yes">{t.withPhoto}</option>
                  <option value="no">{t.withoutPhoto}</option>
                </select>
              </div>

              {/* Price Range Filter */}
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.priceRange}</p>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t.minPrice}
                    value={filterPriceMin}
                    onChange={(e) => setFilterPriceMin(e.target.value)}
                    className="w-1/2 p-2 rounded border text-sm"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t.maxPrice}
                    value={filterPriceMax}
                    onChange={(e) => setFilterPriceMax(e.target.value)}
                    className="w-1/2 p-2 rounded border text-sm"
                  />
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="sm:col-span-2 md:col-span-3">
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.dateRange}</p>
                <div className="flex gap-2 flex-wrap">
                  <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                    <label className="text-xs text-custom-letters">{t.dateFrom}</label>
                    <input
                      type="date"
                      value={filterDateFrom}
                      max={filterDateTo || undefined}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="p-2 rounded border text-sm w-full"
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                    <label className="text-xs text-custom-letters">{t.dateTo}</label>
                    <input
                      type="date"
                      value={filterDateTo}
                      min={filterDateFrom || undefined}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="p-2 rounded border text-sm w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PILLS SECTION */}
          <div className="flex flex-wrap gap-2 mt-3 mb-6">
            {filterCategory.map(catId => {
              const catObj = filterOptions.categories?.find(c => String(c.category_id) === String(catId));
              return catObj && (
                <span key={catId} onClick={() => toggleSelection(catId, filterCategory, setFilterCategory)}
                  className="px-3 py-1 bg-custom-grad1 text-white rounded-full cursor-pointer text-xs md:text-sm whitespace-nowrap">
                  {language === 'el' ? catObj.category_name_el : catObj.category_name} ✕
                </span>
              );
            })}
            {filterCity && (
              <span onClick={() => { setFilterCity(""); setFilterArea([]); }}
                className="px-3 py-1 bg-custom-grad3 text-white rounded-full cursor-pointer text-sm">
                {filterCity} ✕
              </span>
            )}
            {filterArea.map(area => (
              <span key={area} onClick={() => toggleSelection(area, filterArea, setFilterArea)}
                className="px-3 py-1 bg-custom-grad2 text-white rounded-full cursor-pointer text-sm">
                {area} ✕
              </span>
            ))}

            {/* Status pill */}
            {filterStatus !== "all" && (
              <span
                onClick={() => setFilterStatus("all")}
                className="px-3 py-1 bg-custom-grad4 text-white rounded-full cursor-pointer text-sm">
                {filterStatus === "Active" ? t.onlyActive : t.onlyExpired} ✕
              </span>
            )}

            {/* Photo pill */}
            {filterHasPhoto !== "all" && (
              <span
                onClick={() => setFilterHasPhoto("all")}
                className="px-3 py-1 bg-custom-grad1 text-white rounded-full cursor-pointer text-sm">
                {filterHasPhoto === "yes" ? t.withPhoto : t.withoutPhoto} ✕
              </span>
            )}

            {/* Price range pill */}
            {(filterPriceMin !== "" || filterPriceMax !== "") && (
              <span
                onClick={() => { setFilterPriceMin(""); setFilterPriceMax(""); }}
                className="px-3 py-1 bg-custom-grad2 text-white rounded-full cursor-pointer text-sm">
                €{filterPriceMin || "0"} – €{filterPriceMax || "∞"} ✕
              </span>
            )}

            {/* Date range pill */}
            {(filterDateFrom || filterDateTo) && (
              <span
                onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); }}
                className="px-3 py-1 bg-custom-grad3 text-white rounded-full cursor-pointer text-sm">
                {filterDateFrom || "…"} → {filterDateTo || "…"} ✕
              </span>
            )}
          </div>
          {sortVisible && (
            <div className="bg-custom-header p-4 rounded-xl shadow mb-6 w-64">
              <p className="text-sm font-semibold mb-2 text-custom-letters">{t.sortBy}</p>
              <select
                value={sortBy} // Field name to sort by (changed from sortField)
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full p-2 rounded border mb-3"
              >
                <option value="title">{t.name}</option>
                <option value="created_at">{t.datePub}</option>
                <option value="status">{t.status}</option>
                <option value="store_name">{t.store}</option>
                <option value="rating">{t.rating}</option>
                <option value="discount_price">{t.price}</option>                
              </select>

              <select
                value={order} // Sort direction ascending or descending (changed from sortOrder)
                onChange={(e) => setOrder(e.target.value)}
                className="w-full p-2 rounded border"
              >
                <option value="asc">{getOrderLabel("asc")}</option>
                <option value="desc">{getOrderLabel("desc")}</option>
              </select>
            </div>
          )}
          <div className="w-full max-w-6xl bg-white/40 rounded-2xl shadow-lg  p-4 sm:p-6 backdrop-blur-sm">

            {/*Table for large screens */}
            <div className="hidden xl:block overflow-x-auto">
              <table className="min-w-full text-sm text-custom-letters text-center border-collapse">
                <thead className="border-b font-medium text-custom-letters">
                  <tr>
                    <th className="px-4 py-3">{t.discounts}</th>
                    <th className="px-4 py-3">{t.datePub}</th>
                    <th className="px-4 py-3">{t.status}</th>
                    <th className="px-4 py-3">{t.location}</th>
                    <th className="px-4 py-3">{t.rating}</th>
                    <th className="px-4 py-3">{t.actions}</th>
                  </tr>
                </thead>

                <tbody>
                  {favorites.length > 0 ? (
                    favorites.map((item) => (
                      <tr key={item.discount_id} className="border-b hover:bg-custom-background transition">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleViewDiscount(item.discount_id)}
                            className="font-medium text-custom-grad4 hover:underline cursor-pointer text-left w-full"
                          >
                            {item.title}
                          </button>
                        </td>
                        <td className="px-4 py-3">{new Date(item.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-custom-grad1 font-semibold">{getStatusLabel(item.status)}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleShowOnMap(item)}
                            className="text-custom-grad4 hover:underline cursor-pointer w-full"
                          >
                            <span className="flex items-center justify-center gap-1">
                              <MapPin size={13} /> {item.store_name}
                            </span>
                          </button>
                        </td>
                        <td className="px-4 py-3">{item.rating}/5</td>
                        <td className="px-4 py-3 flex justify-center items-center">
                          <button
                            onClick={() => handleRemoveFavorite(item.discount_id)}
                            className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full hover:opacity-90 transition flex items-center justify-center"
                            title={t.revFavs}>
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-custom-letters py-6 italic">
                        {t.noFavs}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Collapsible cards for small screens */}
            <div className="block xl:hidden">
              {favorites.length > 0 ? (
                favorites.map((item) => {
                  const isExpanded = expandedRow === item.discount_id;

                  return (
                    <div
                      key={item.discount_id}
                      className="border border-gray-300 rounded-xl mb-4 bg-custom-background/60 shadow-sm overflow-hidden"
                    >
                      {/* Header Area */}
                      <div className="flex items-center px-3 py-3 gap-2">

                        {/*(Chevron) */}
                        <button
                          onClick={() => toggleExpand(item.discount_id)}
                          className="p-1 hover:bg-gray-200 rounded-full transition-transform duration-200 flex-shrink-0"
                          style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                        >
                          <ChevronRight size={20} className="text-gray-500" />
                        </button>

                        {/* (Navigation) */}
                        <div className="flex-1 min-w-0 text-left">
                          <button
                            onClick={() => handleViewDiscount(item.discount_id)}
                            className="block font-semibold text-custom-grad4 hover:underline cursor-pointer truncate text-sm text-left w-full"
                          >
                            {item.title}
                          </button>
                        </div>

                        {/*DELETE (Action) */}
                        <div className="flex-shrink-0 ml-2">
                          <button
                            onClick={() => handleRemoveFavorite(item.discount_id)}
                            className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-2 rounded-full hover:opacity-90 transition flex items-center justify-center shadow-sm"
                            title={t.revFavs}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-10 pb-4 text-xs text-custom-letters text-left border-t border-gray-100 pt-3 bg-white/30 animate-in fade-in slide-in-from-top-1 duration-200">
                          <p className="mb-1.5">
                            <span className="font-semibold text-custom-letters">{t.datePub}:</span>{" "}
                            {new Date(item.created_at).toLocaleDateString()}
                          </p>
                          <p className="mb-1.5">
                            <span className="font-semibold text-custom-letters">{t.status}:</span>{" "}
                            <span className="text-custom-grad1 font-bold">{getStatusLabel(item.status)}</span>
                          </p>
                          <p className="mb-1.5 flex items-center gap-1">
                            <span className="font-semibold text-custom-letters whitespace-nowrap">{t.location}:</span>
                            <button
                              onClick={() => handleShowOnMap(item)}
                              className="text-custom-grad4 underline cursor-pointer font-medium flex items-center gap-1 truncate"
                            >
                              <MapPin size={13} className="flex-shrink-0" />
                              <span className="truncate">{item.store_name}</span>
                            </button>
                          </p>
                          <p>
                            <span className="font-semibold text-custom-letters">{t.rating}:</span>{" "}
                            <span>
                              {item.rating}/5
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-custom-letters py-6 italic">
                  {t.noFavs}
                </p>
              )}
            </div>
          </div>

          <div ref={loaderRef} className="h-10 w-full max-w-6xl" />
          {loadingMore && <div className="text-center mt-2">{t.loadMore}</div>}
          {!hasMore && !loading && favorites.length > 0 && (
            <div className="text-center mt-2 text-custom-letters">{t.nmDiscounts}</div>
          )}
        </main>
      </div>

      {infoPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="favorites-info-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="favorites-info-title" className="text-lg font-semibold text-custom-letters mb-3">{infoPopup.title}</h3>
            <p className="text-sm text-custom-letters mb-6">{infoPopup.msg}</p>
            <button
              onClick={() => setInfoPopup(null)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Favorites;
